'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';

const aboutSections = [
  { id: 'about', label: 'About Us', href: '/about' },
  { id: 'history', label: 'History', href: '/about/history' },
  { id: 'mission', label: 'Mission', href: '/about/mission' },
  { id: 'vision', label: 'Vision', href: '/about/vision' },
  { id: 'membership', label: 'Membership', href: '/about/membership' },
  { id: 'quality', label: 'Quality Management', href: '/about/quality-management' },
  { id: 'ethics', label: 'Business Ethics', href: '/about/business-ethics' },
];

const qualityItems = [
  { icon: '🖥️', title: 'ERP System', desc: 'Microsoft Dynamics Nav with vertical solution NaviTrans — a comprehensive enterprise resource planning system managing all logistics operations.' },
  { icon: '🌐', title: 'e-WTO Portal', desc: '24/7 e-Services to registered WTO customers at the company website, including detailed shipment history, financial statements, invoice downloads, and more.' },
  { icon: '📍', title: 'Real-time Track & Trace', desc: 'Live shipment tracking available around the clock through the customer portal, providing full visibility at every stage.' },
  { icon: '📋', title: 'e-Booking Order', desc: 'Online booking system allowing customers to place and manage freight bookings digitally, 24/7.' },
  { icon: '💰', title: 'e-Rate Request', desc: 'Digital rate request system for fast and transparent freight quotations tailored to customer needs.' },
  { icon: '📄', title: 'e-Shipping Instructions', desc: 'Electronic submission of shipping instructions, streamlining documentation and reducing processing time.' },
  { icon: '🔗', title: 'EDI Integration', desc: 'EDI projects realized with main partners and customers for seamless data exchange and process automation.' },
  { icon: '✈️', title: 'Licensed IATA Cargo Agent', desc: 'WTO holds a valid IATA cargo agent license, ensuring compliance with international air freight standards.' },
  { icon: '🏆', title: 'ISO 9001:2015 Certified', desc: 'WTO is certified to the ISO 9001:2015 quality management standard, demonstrating commitment to consistent, high-quality service delivery.' },
];

export default function QualityManagementPage() {
  return (
    <>
      <Header />
      <main className="min-h-screen" style={{ background: '#F4F6F9', color: '#0F2340' }}>
        {/* Hero */}
        <div className="relative pt-32 pb-16 px-6" style={{ background: 'linear-gradient(180deg, #0F2340 0%, #1A3558 100%)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="max-w-5xl mx-auto">
            <Link href="/about" className="inline-flex items-center gap-2 text-sm font-body mb-4 transition-colors" style={{ color: '#7EB8F7' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#7EB8F7'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
              The Company
            </Link>
            <p className="text-xs uppercase tracking-widest font-body mb-3" style={{ color: '#7EB8F7' }}>World Transport Overseas</p>
            <h1 className="text-4xl lg:text-5xl font-bold font-body mb-4" style={{ color: '#FFFFFF' }}>Quality Management</h1>
            <p className="text-lg font-body max-w-2xl" style={{ color: 'rgba(200,216,240,0.7)' }}>
              ISO 9001:2015 certified — committed to excellence in every process.
            </p>
          </div>
        </div>
        {/* Sub-nav */}
        <div className="sticky top-20 z-30 px-6 py-3" style={{ background: 'rgba(255,255,255,0.97)', borderBottom: '1px solid rgba(15,35,64,0.1)', backdropFilter: 'blur(12px)' }}>
          <div className="max-w-5xl mx-auto flex flex-wrap gap-1">
            {aboutSections?.map((s) => (
              <Link key={s?.id} href={s?.href}
                className="px-4 py-2 rounded-full text-sm font-body font-medium transition-all duration-200"
                style={s?.id === 'quality'
                  ? { background: '#1B6B8A', color: '#FFFFFF', border: '1px solid rgba(27,107,138,0.5)' }
                  : { background: 'transparent', color: '#3A5070', border: '1px solid rgba(15,35,64,0.15)' }}
                onMouseEnter={(e) => { if (s?.id !== 'quality') { e.currentTarget.style.background = 'rgba(27,107,138,0.1)'; e.currentTarget.style.color = '#0F2340'; } }}
                onMouseLeave={(e) => { if (s?.id !== 'quality') { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#3A5070'; } }}
              >{s?.label}</Link>
            ))}
          </div>
        </div>
        {/* Content */}
        <div className="max-w-5xl mx-auto px-6 py-16">
          {/* ISO badge */}
          <div className="flex items-center gap-6 rounded-3xl p-8 mb-12" style={{ background: '#0F2340', border: '1px solid rgba(27,107,138,0.3)' }}>
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0 text-4xl" style={{ background: 'rgba(27,107,138,0.3)', border: '1px solid rgba(27,107,138,0.5)' }}>
              🏆
            </div>
            <div>
              <h2 className="text-xl font-bold font-body mb-1" style={{ color: '#FFFFFF' }}>ISO 9001:2015 Certified</h2>
              <p className="text-sm font-body leading-relaxed" style={{ color: 'rgba(200,216,240,0.85)' }}>
                World Transport Overseas holds the ISO 9001:2015 certification, the internationally recognized standard for quality management systems. This certification reflects our commitment to consistent quality, customer satisfaction, and continuous improvement across all operations.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {qualityItems?.map((item) => (
              <div key={item?.title} className="rounded-2xl p-6" style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)', boxShadow: '0 2px 8px rgba(15,35,64,0.06)' }}>
                <div className="text-3xl mb-4">{item?.icon}</div>
                <h3 className="text-base font-bold font-body mb-2" style={{ color: '#0F2340' }}>{item?.title}</h3>
                <p className="text-sm font-body leading-relaxed" style={{ color: '#3A5070' }}>{item?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </>
  );
}
